/**
 * The cells shared by both drill-downs.
 *
 * The naming rule from design §4 lives here: `correctnessScore` is the
 * STUDENT's accuracy, `assessmentQuality` is ours in judging them. Rendering
 * both through the same components is what keeps the two columns labelled
 * apart wherever they appear.
 */

import { useEffect, useState } from 'react';
import type { AnalysisQualityFacts, StudentAnswer, VerdictValue } from '../../../types/assignmentQuality';

export function AnswerCell({ answer, fallback }: { answer: StudentAnswer; fallback?: string }) {
    const [zoomed, setZoomed] = useState(false);
    const hasAnything = answer.text || answer.imageUrl || answer.selectedOptions?.length;
    if (!hasAnything) return <span className="aq-muted">{fallback ?? '—'}</span>;
    return (
        <div className="aq-answer">
            {answer.selectedOptions?.length ? <div><b>{answer.selectedOptions.join(', ')}</b></div> : null}
            {answer.text ? <div>{answer.text.slice(0, 400)}</div> : null}
            {answer.imageUrl && (
                // A row thumbnail is too small to check a transcription against
                // the working, which is the one thing this page exists for.
                <button
                    type="button"
                    className="aq-thumb"
                    onClick={e => { e.stopPropagation(); setZoomed(true); }}
                    title="Click to enlarge"
                >
                    <img src={answer.imageUrl} alt="Student work" loading="lazy" />
                </button>
            )}
            {zoomed && answer.imageUrl && (
                <ImageLightbox src={answer.imageUrl} onClose={() => setZoomed(false)} />
            )}
        </div>
    );
}

/**
 * Full-size view of one piece of student work.
 *
 * Closes on backdrop click, the button, or Escape; the image itself swallows
 * clicks so dragging to read doesn't dismiss it. Rendered inline rather than
 * through a portal — the overlay is `position: fixed`, so the table's scroll
 * container cannot clip it.
 */
function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="aq-lightbox" role="dialog" aria-modal="true" aria-label="Student work"
            onClick={e => { e.stopPropagation(); onClose(); }}>
            <div className="aq-lightbox-bar" onClick={e => e.stopPropagation()}>
                <a href={src} target="_blank" rel="noreferrer" className="aq-link">open original ↗</a>
                <button type="button" className="aq-lightbox-close" onClick={onClose} aria-label="Close">×</button>
            </div>
            <img src={src} alt="Student work" onClick={e => e.stopPropagation()} />
        </div>
    );
}

const CORRECTNESS_TONE: Record<string, string> = {
    correct: 'ok',
    partially_correct: 'warn',
    incorrect: 'bad',
};

export function CorrectnessBadge({ value }: { value?: string }) {
    if (!value) return <span className="aq-muted">—</span>;
    return <span className={`aq-badge ${CORRECTNESS_TONE[value] ?? ''}`}>{value.replace(/_/g, ' ')}</span>;
}

export function ScoreCell({ value }: { value?: number }) {
    return value === undefined ? <span className="aq-muted">—</span> : <span>{value}</span>;
}

/**
 * Analysis health: what the machine noticed. Fired post-call checks and the
 * run's own lifecycle — no human judgement.
 *
 * A thumbs-down used to live here, back when there was nowhere else to put it.
 * It now has its own column, and showing it twice made a single opinion look
 * like two independent signals.
 */
export function HealthCell({ facts }: { facts: AnalysisQualityFacts }) {
    return (
        <div className="aq-metric">
            {facts.status === 'failed' && (
                <span className="aq-badge bad" title={facts.failureMessage}>{facts.failureKind || 'failed'}</span>
            )}
            {facts.status === 'analyzing' && <span className="aq-badge warn">analyzing</span>}
            {/* Not pills: the pill's padding is the least of it — these names run
                to 30 characters, so the width is the text. Plain wrapped lines in
                a capped column, keeping the amber to carry "something fired". */}
            {facts.qualityChecks.map(check => (
                <span key={check} className="aq-check" title="Post-call quality check fired">
                    {check.replace(/_/g, ' ')}
                </span>
            ))}
            {facts.status !== 'failed' && facts.status !== 'analyzing'
                && facts.qualityChecks.length === 0
                && <span className="aq-muted">clean</span>}
        </div>
    );
}

const VERDICT_TONE: Record<VerdictValue, string> = { good: 'ok', acceptable: 'warn', bad: 'bad' };

export function VerdictBadge({ value, label, count }: { value?: VerdictValue; label?: string; count?: number }) {
    if (!value) return <span className="aq-muted">—</span>;
    const text = label ? `${label}: ${value}` : value;
    return <span className={`aq-badge ${VERDICT_TONE[value]}`}>{count === undefined ? text : `${text} (${count})`}</span>;
}

/**
 * How good OUR assessment was, split by dimension.
 *
 * Empty means nobody judged it — never "fine". A judged cell shows the overall
 * verdict plus the two dimensions, and the critique is the tooltip: an admin
 * scanning a column wants the word, and the sentence only when they stop.
 */
export function AssessmentQualityCell({ facts, onJudge, judging }: {
    facts: AnalysisQualityFacts;
    /** Runs the judge over this assignment. Omitted where judging isn't offered. */
    onJudge?: () => void;
    judging?: boolean;
}) {
    const quality = facts.assessmentQuality;
    if (!quality) {
        if (judging) return <span className="aq-muted">judging…</span>;
        // An unjudged cell is the one place the judge is worth offering: the
        // admin is looking straight at the gap.
        return onJudge
            ? <button type="button" className="aq-link"
                onClick={e => { e.stopPropagation(); onJudge(); }}
                title="Promote this assignment and run the LLM judge over its analyses">judge →</button>
            : <span className="aq-muted" title="Populated once this assignment is judged">not judged</span>;
    }
    return (
        <div className="aq-metric" title={quality.critique}>
            <VerdictBadge value={quality.verdict} />
            <VerdictBadge value={quality.accuracy} label="acc" />
            <VerdictBadge value={quality.approach} label="app" />
            {quality.evaluator === 'human' && <span className="aq-badge">human</span>}
            {quality.failureTags.map(tag => <span key={tag} className="aq-badge warn">{tag}</span>)}
        </div>
    );
}

/** Reading first, then what was done with it — the order the failure travelled. */
const ROLE_LABEL: Record<string, string> = {
    reading: 'read', assessment: 'assess', scoring: 'score', combined: 'read+assess',
};

/**
 * Per-step judging, from the table.
 *
 * The row-level thumbs beside this one cannot say which of the two prompt
 * executions failed, which is the only thing worth recording about a bad
 * analysis. This opens the same drawer the bench uses, against this row.
 *
 * Verdicts already recorded show as chips rather than behind the click: the
 * point of the column is that the next reader sees reading was judged apart
 * from assessment without opening anything.
 */
export function StepVerdictsCell({ facts, onOpen, pending }: {
    facts: AnalysisQualityFacts;
    onOpen: () => void;
    pending?: boolean;
}) {
    const steps = facts.stepVerdicts ?? [];
    return (
        <div className="aq-steps" onClick={e => e.stopPropagation()}>
            {steps.length > 0 && (
                <div className="aq-steps-chips">
                    {steps.map((s, i) => (
                        <span key={`${s.stepType}-${i}`} className={`aq-badge ${VERDICT_TONE[s.verdict]}`}
                            title={s.failureTags.length ? s.failureTags.join(', ') : s.stepType}>
                            {ROLE_LABEL[s.role] ?? s.role}: {s.verdict}
                        </span>
                    ))}
                </div>
            )}
            <button type="button" className="aq-link" disabled={pending} onClick={onOpen}>
                {pending ? 'opening…' : steps.length ? 'edit steps' : 'judge steps'}
            </button>
        </div>
    );
}

/**
 * Your thumbs + note on one analysis, written from the table.
 *
 * Reviewing is a two-step thought — a verdict, then why — so the thumbs commit
 * immediately and the note is a second, optional write. Waiting for prose
 * before recording the judgement loses the judgement when someone gets
 * distracted, which is most of the time.
 *
 * One review per admin: the write path replaces yours and leaves everyone
 * else's, so the cell shows yours as state and the rest as context.
 */
export function ReviewCell({ facts, adminId, onSubmit, pending }: {
    facts: AnalysisQualityFacts;
    adminId?: string;
    onSubmit: (rating: 'positive' | 'negative', comment?: string) => void;
    pending?: boolean;
}) {
    // The client ships separately from the backend: a server that predates
    // `reviews` sends rows without it, and a crashed table is a worse answer
    // than an empty review cell.
    const reviews = facts.reviews ?? [];
    const mine = reviews.find(r => r.by === adminId);
    const others = reviews.filter(r => r.by !== adminId);
    // A thumbs already recorded against this analysis — by another admin, or by
    // the student — is the row's existing verdict. Showing the buttons blank
    // beside a 👎 in Analysis health invites a second, contradictory one.
    const standing = mine?.rating
        ?? others[0]?.rating
        ?? (facts.thumbsDown ? 'negative' as const : undefined);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(mine?.comment ?? '');

    return (
        // The row navigates on click; everything in here is an action ON the
        // row, so none of it should also open the analysis.
        <div className="aq-review" onClick={e => e.stopPropagation()}>
            <div className="aq-review-thumbs">
                <button
                    type="button"
                    className={`aq-thumb-btn${standing === 'positive' ? (mine ? ' on' : ' on inherited') : ''}`}
                    disabled={pending}
                    title="This analysis is good"
                    onClick={() => onSubmit('positive', mine?.comment)}
                >👍</button>
                <button
                    type="button"
                    className={`aq-thumb-btn${standing === 'negative' ? (mine ? ' bad-on' : ' bad-on inherited') : ''}`}
                    disabled={pending}
                    title="This analysis is wrong or misleading"
                    onClick={() => onSubmit('negative', mine?.comment)}
                >👎</button>
                {(mine || standing) && !editing && (
                    <button type="button" className="aq-link aq-review-note"
                        onClick={() => { setDraft(mine?.comment ?? ''); setEditing(true); }}>
                        {mine?.comment ? 'edit note' : '+ note'}
                    </button>
                )}
            </div>

            {mine?.comment && !editing && <div className="aq-review-text">{mine.comment}</div>}

            {editing && (
                <div className="aq-review-edit">
                    <textarea
                        value={draft}
                        rows={2}
                        placeholder="What is wrong with this analysis?"
                        onChange={e => setDraft(e.target.value)}
                    />
                    <div className="aq-review-actions">
                        <button type="button" className="btn btn-primary" disabled={pending}
                            onClick={() => {
                                // The note rides on the existing rating; a note
                                // without a verdict has nothing to attach to.
                                onSubmit(mine?.rating ?? 'negative', draft.trim() || undefined);
                                setEditing(false);
                            }}>Save</button>
                        <button type="button" className="btn btn-secondary"
                            onClick={() => setEditing(false)}>Cancel</button>
                    </div>
                </div>
            )}

            {others.map(r => (
                <div key={r.by} className="aq-review-other" title={`${r.by} · ${r.at.slice(0, 10)}`}>
                    {r.rating === 'positive' ? '👍' : '👎'} {r.comment ?? <span className="aq-muted">no note</span>}
                </div>
            ))}
        </div>
    );
}
